/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Intent: cajero personaliza el ítem en 2–4 toques (estilo carta delivery).
 * Hierarchy: grupos → filas táctiles → resumen sticky → CTA con precio.
 * Depth: bordes ticket/POS; selección = borde accent + check/radio.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import type { Product } from '../../../catalog/domain-api';
import type { MenuOptionGroup, SelectedMenuOption } from '../../../catalog/domain-api';
import { formatOptionsLabel, isMenuGroupFullyUnavailable, noneChoiceLabel, shouldShowNoneChoiceRow, toSelectedMenuOption, unitPriceWithOptions, validateMenuSelection } from '../../../catalog/domain-api';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../../shared/layout/breakpoints';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { ModalShell } from '../../../shared/modals/ModalShell'
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { posSharedUi as posUi } from '../../../shared/ui/posSharedUi';
import { showInfo } from '../../../shared/ui/appToast';

type Props = {
  visible: boolean;
  product: Product | null;
  groups: MenuOptionGroup[];
  loading?: boolean;
  /** Gap 4: pre-selección al editar una línea existente. */
  initialSelected?: SelectedMenuOption[];
  /** Gap 4: nota inicial al editar una línea existente. */
  initialLineNote?: string | null;
  onCancel: () => void;
  onConfirm: (selected: SelectedMenuOption[], lineNote: string) => void;
};

export function PosMenuOptionsModal({
  visible,
  product,
  groups,
  loading = false,
  initialSelected,
  initialLineNote,
  onCancel,
  onConfirm,
}: Props) {
  const { width } = useWindowDimensions();
  const isWide = width >= CONTENT_WIDE_BREAKPOINT;
  const [selected, setSelected] = useState<SelectedMenuOption[]>([]);
  const [lineNote, setLineNote] = useState('');
  const isConfirmingRef = useRef(false);

  useEffect(() => {
    if (!visible) return;
    // Gap 4: si hay pre-selección (edit mode), usarla; si no, defaults.
    if (initialSelected && initialSelected.length > 0) {
      setSelected(initialSelected);
    } else {
      const initial: SelectedMenuOption[] = [];
      for (const g of groups) {
        if (g.selectionMin >= 1 && g.selectionMax === 1) {
          const first = g.choices.find((c) => c.isAvailable);
          if (first) {
            initial.push(toSelectedMenuOption(g, first));
          }
        }
      }
      setSelected(initial);
    }
    setLineNote(initialLineNote ?? '');
  }, [visible, groups, initialSelected, initialLineNote]);

  const unitPrice = useMemo(
    () => unitPriceWithOptions(product?.price ?? 0, selected),
    [product?.price, selected],
  );

  const blockedGroup = useMemo(
    () => groups.find((g) => isMenuGroupFullyUnavailable(g)) ?? null,
    [groups],
  );

  const selectionError = useMemo(
    () => (loading || groups.length === 0 || blockedGroup ? 'blocked' : validateMenuSelection(groups, selected)),
    [blockedGroup, groups, loading, selected],
  );

  const canConfirm = !loading && groups.length > 0 && !blockedGroup && selectionError == null;

  const summary = formatOptionsLabel(selected);

  const clearGroup = (group: MenuOptionGroup) => {
    setSelected((prev) => prev.filter((s) => s.groupId !== group.id));
  };

  const toggleSingle = (group: MenuOptionGroup, choiceId: string) => {
    const choice = group.choices.find((c) => c.id === choiceId);
    if (!choice || !choice.isAvailable) return;
    setSelected((prev) => {
      const without = prev.filter((s) => s.groupId !== group.id);
      return [...without, toSelectedMenuOption(group, choice)];
    });
  };

  const toggleMulti = (group: MenuOptionGroup, choiceId: string) => {
    const choice = group.choices.find((c) => c.id === choiceId);
    if (!choice || !choice.isAvailable) return;
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id && s.choiceId === choiceId);
      if (existing) {
        return prev.filter((s) => !(s.groupId === group.id && s.choiceId === choiceId));
      }
      const groupQty = prev.filter((s) => s.groupId === group.id).reduce((n, s) => n + s.qty, 0);
      if (groupQty >= group.selectionMax) {
        showInfo('Máximo', `Podés elegir hasta ${group.selectionMax} en ${group.name}`);
        return prev;
      }
      return [...prev, toSelectedMenuOption(group, choice)];
    });
  };

  const bumpMultiQty = (group: MenuOptionGroup, choiceId: string, delta: number) => {
    const choice = group.choices.find((c) => c.id === choiceId);
    if (!choice || !choice.isAvailable) return;
    setSelected((prev) => {
      const existing = prev.find((s) => s.groupId === group.id && s.choiceId === choiceId);
      if (!existing) {
        if (delta < 0) return prev;
        const groupQty = prev.filter((s) => s.groupId === group.id).reduce((n, s) => n + s.qty, 0);
        if (groupQty >= group.selectionMax) {
          showInfo('Máximo', `Podés elegir hasta ${group.selectionMax} en ${group.name}`);
          return prev;
        }
        return [...prev, toSelectedMenuOption(group, choice)];
      }
      const nextQty = existing.qty + delta;
      if (nextQty <= 0) {
        return prev.filter((s) => !(s.groupId === group.id && s.choiceId === choiceId));
      }
      if (nextQty > choice.maxQty) {
        showInfo('Máximo', `Máximo ${choice.maxQty} de ${choice.name}`);
        return prev;
      }
      const othersQty = prev
        .filter((s) => s.groupId === group.id && s.choiceId !== choiceId)
        .reduce((n, s) => n + s.qty, 0);
      if (othersQty + nextQty > group.selectionMax) {
        showInfo('Máximo', `Podés elegir hasta ${group.selectionMax} en ${group.name}`);
        return prev;
      }
      return prev.map((s) =>
        s.groupId === group.id && s.choiceId === choiceId ? { ...s, qty: nextQty } : s,
      );
    });
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    if (isConfirmingRef.current) return;
    isConfirmingRef.current = true;
    try {
    const err = validateMenuSelection(groups, selected);
    if (err) {
      showInfo('Completá opciones', err);
      return;
    }
    onConfirm(selected, lineNote.trim());
    } finally {
      isConfirmingRef.current = false;
    }
  };

  const renderNoneRow = (group: MenuOptionGroup, choiceCount: number) => {
    if (!shouldShowNoneChoiceRow(group)) return null;
    const nonePicked = !selected.some((s) => s.groupId === group.id);
    const single = group.selectionMax === 1;
    return (
      <TouchableOpacity
        key={`${group.id}-none`}
        style={[
          styles.choiceRow,
          nonePicked && styles.choiceSelected,
          choiceCount > 0 && styles.choiceBorder,
        ]}
        onPress={() => clearGroup(group)}
        accessibilityRole={single ? 'radio' : 'checkbox'}
        accessibilityState={{ checked: nonePicked, selected: nonePicked }}
      >
        <View
          style={[
            single ? styles.radio : styles.checkbox,
            nonePicked && (single ? styles.radioOn : styles.checkboxOn),
          ]}
        >
          {nonePicked && !single ? <View style={styles.checkboxMark} /> : null}
          {nonePicked && single ? <View style={styles.radioDot} /> : null}
        </View>
        <Text style={styles.choiceName} numberOfLines={2}>
          {noneChoiceLabel(group.name)}
        </Text>
        <Text style={styles.choicePrice}>Incluido</Text>
      </TouchableOpacity>
    );
  };

  return (
    <ModalShell
      visible={visible}
      onClose={onCancel}
      title="Elegí opciones"
      subtitle={product?.name}
      keyboardAvoiding
      widthVariant={isWide ? 'standard' : 'compact'}
      scrollContentStyle={styles.bodyGap}
      footer={
        <View style={styles.footerBlock}>
          {summary ? (
            <Text style={styles.stickySummary} numberOfLines={2}>
              {summary}
            </Text>
          ) : null}
          <View style={modalExperience.footerActions}>
            <TouchableOpacity
              style={[posUi.secondaryButton, posUi.modalButtonHalf]}
              onPress={onCancel}
              accessibilityRole="button"
              accessibilityLabel="Cancelar opciones"
            >
              <Text style={posUi.secondaryButtonText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                posUi.primaryButton,
                posUi.modalButtonHalf,
                !canConfirm && styles.btnDisabled,
              ]}
              onPress={handleConfirm}
              disabled={!canConfirm}
              accessibilityRole="button"
            >
              <Text style={posUi.primaryButtonText}>
                Agregar · {formatPosMoney(unitPrice)}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      }
    >
      {loading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator color={colors.accent} />
          <Text style={styles.loadingText}>Cargando opciones…</Text>
        </View>
      ) : blockedGroup ? (
        <View style={styles.emptyBox}>
          <Text style={styles.emptyTitle}>Se acabaron las opciones</Text>
          <Text style={styles.emptySub}>
            «{blockedGroup.name}» está agotado. No lo podés agregar ahora.
          </Text>
        </View>
      ) : (
        <>
          {groups.map((group) => {
            const single = group.selectionMax === 1;
            const required = group.selectionMin > 0;
            return (
              <View key={group.id} style={styles.group}>
                <View style={styles.groupHeader}>
                  <Text style={styles.groupTitle}>{group.name}</Text>
                  <StatusPill
                    tone={required ? 'warning' : 'neutral'}
                    label={
                      required
                        ? single
                          ? 'Elegí 1'
                          : `Mín. ${group.selectionMin}`
                        : single
                          ? 'Opcional'
                          : `Hasta ${group.selectionMax}`
                    }
                  />
                </View>
                <View style={modalExperience.listContainer}>
                  {renderNoneRow(group, group.choices.length)}
                  {group.choices.map((choice, index) => {
                    const pick = selected.find(
                      (s) => s.groupId === group.id && s.choiceId === choice.id,
                    );
                    const picked = !!pick;
                    const useStepper = !single && choice.maxQty > 1;
                    const rowStyle = [
                      styles.choiceRow,
                      picked && styles.choiceSelected,
                      index < group.choices.length - 1 && styles.choiceBorder,
                      !choice.isAvailable && styles.choiceDisabled,
                    ];
                    const marker = (
                      <View
                        style={[
                          single ? styles.radio : styles.checkbox,
                          picked && (single ? styles.radioOn : styles.checkboxOn),
                        ]}
                      >
                        {picked && !single ? <View style={styles.checkboxMark} /> : null}
                        {picked && single ? <View style={styles.radioDot} /> : null}
                      </View>
                    );
                    const priceLabel = (
                      <Text
                        style={[
                          styles.choicePrice,
                          !choice.isAvailable && styles.choicePriceAgotado,
                        ]}
                      >
                        {!choice.isAvailable
                          ? 'Agotado'
                          : choice.priceDelta === 0
                            ? 'Incluido'
                            : `+${formatPosMoney(choice.priceDelta)}`}
                      </Text>
                    );
                    const nameEl = (
                      <Text
                        style={[
                          styles.choiceName,
                          !choice.isAvailable && styles.choiceNameAgotado,
                        ]}
                        numberOfLines={2}
                      >
                        {choice.name}
                      </Text>
                    );
                    if (useStepper && picked) {
                      return (
                        <View key={choice.id} style={rowStyle}>
                          {marker}
                          {nameEl}
                          <View style={styles.stepper}>
                            <TouchableOpacity
                              style={styles.stepBtn}
                              onPress={() => bumpMultiQty(group, choice.id, -1)}
                              accessibilityRole="button"
                            >
                              <Text style={styles.stepBtnText}>−</Text>
                            </TouchableOpacity>
                            <Text style={styles.stepQty}>{pick?.qty ?? 1}</Text>
                            <TouchableOpacity
                              style={styles.stepBtn}
                              onPress={() => bumpMultiQty(group, choice.id, 1)}
                              accessibilityRole="button"
                            >
                              <Text style={styles.stepBtnText}>+</Text>
                            </TouchableOpacity>
                          </View>
                          {priceLabel}
                        </View>
                      );
                    }
                    return (
                      <TouchableOpacity
                        key={choice.id}
                        style={rowStyle}
                        disabled={!choice.isAvailable}
                        onPress={() => {
                          if (single) {
                            toggleSingle(group, choice.id);
                            return;
                          }
                          if (useStepper) {
                            bumpMultiQty(group, choice.id, 1);
                            return;
                          }
                          toggleMulti(group, choice.id);
                        }}
                        accessibilityRole={single ? 'radio' : 'checkbox'}
                        accessibilityState={{ checked: picked, selected: picked }}
                      >
                        {marker}
                        {nameEl}
                        {priceLabel}
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            );
          })}

          <Text style={styles.noteLabel}>Nota para cocina</Text>
          <TextInput
            style={[modalExperience.formInput, styles.noteInput]}
            value={lineNote}
            onChangeText={setLineNote}
            placeholder="Ej. sin cebolla"
            placeholderTextColor={colors.textMuted}
            accessibilityLabel="Nota para cocina"
          />
        </>
      )}
    </ModalShell>
  );
}

const styles = StyleSheet.create({
  bodyGap: { gap: spacing.md, paddingBottom: spacing.sm },
  loadingBox: {
    minHeight: 160,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  loadingText: { ...typography.caption, color: colors.textMuted },
  emptyBox: {
    minHeight: 140,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.lg,
  },
  emptyTitle: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
    textAlign: 'center',
  },
  emptySub: {
    ...typography.caption,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 18,
  },
  group: {
    gap: spacing.xs,
    padding: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    backgroundColor: ticketColors.paper,
  },
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    marginBottom: 2,
  },
  groupTitle: {
    ...typography.body,
    fontWeight: '700',
    color: colors.textPrimary,
    flex: 1,
  },
  choiceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minHeight: 52,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    backgroundColor: ticketColors.tile,
  },
  choiceBorder: {},
  choiceSelected: {
    borderColor: ticketColors.brandBorderSoft,
    backgroundColor: ticketColors.brandSoft,
  },
  choiceDisabled: { opacity: 0.55 },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: ticketColors.rule,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioOn: { borderColor: ticketColors.gold },
  radioDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: ticketColors.gold,
  },
  checkbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: ticketColors.rule,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper,
  },
  checkboxOn: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  checkboxMark: {
    width: 10,
    height: 6,
    borderLeftWidth: 2,
    borderBottomWidth: 2,
    borderColor: colors.surface,
    transform: [{ rotate: '-45deg' }, { translateY: -1 }],
  },
  choiceName: {
    ...typography.body,
    color: colors.textPrimary,
    flex: 1,
  },
  choiceNameAgotado: {
    textDecorationLine: 'line-through',
    color: colors.textMuted,
  },
  choicePrice: {
    ...typography.body,
    color: ticketColors.ink2,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    fontFamily: ticketFonts.mono,
  },
  choicePriceAgotado: {
    color: colors.danger,
    fontWeight: '700',
  },
  stepper: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  stepBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ticketColors.paper,
  },
  stepBtnText: { ...typography.body, fontWeight: '700', color: colors.textPrimary },
  stepQty: {
    ...typography.body,
    fontWeight: '700',
    minWidth: 18,
    textAlign: 'center',
    fontVariant: ['tabular-nums'],
  },
  noteLabel: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  noteInput: { marginTop: 0 },
  footerBlock: { gap: spacing.xs, width: '100%' },
  stickySummary: {
    ...typography.caption,
    color: colors.textSecondary,
    fontWeight: '600',
    paddingHorizontal: 2,
    marginBottom: 2,
  },
  btnDisabled: { opacity: 0.55 },
});
